import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import SignoutHeader from "../../subcomponents/SignoutHeader";
import {
  PersonAddAlt1Outlined,
  PersonOutlineOutlined,
  DashboardCustomizeOutlined,
  PaymentOutlined,
  LocalAtmOutlined,
  AssignmentOutlined,
  PendingActionsOutlined,
  HealthAndSafetyOutlined,
  Person,
  EventNoteOutlined
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { EHR } from "../../types";

export default function SettingsLayout({
  children
}: {
  children: React.ReactNode;
}) {
  const drawerWidth = 300; // Match DashboardSidebar drawer width

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: { xs: 0, lg: `${drawerWidth}px` },
        right: 0,
        bottom: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column"
      }}
    >
      <Box
        sx={{
          position: "fixed",
          top: { xs: 0, lg: "40px" },
          left: { xs: 0, lg: `${drawerWidth + 40}px` },
          right: { xs: 0, lg: "40px" },
          zIndex: 1000
        }}
      >
        <SignoutHeader />
      </Box>
      <Box
        sx={{
          position: "fixed",
          top: { xs: "64px", lg: "104px" }, // Adjust based on SignoutHeader height (+ main padding on lg)
          left: { xs: 0, lg: `${drawerWidth}px` },
          right: 0,
          bottom: 0,
          display: "flex",
          flexDirection: "column"
        }}
      >
        <Typography variant="h6" sx={{ padding: "16px 0 16px 24px" }}>
          Settings
        </Typography>
        <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
          <Box
            sx={{
              position: "fixed",
              left: { xs: 0, lg: `${drawerWidth}px` },
              top: { xs: "120px", lg: "160px" }, // Adjust based on header + title height (+ main padding on lg)
              bottom: 0,
              width: "280px",
              overflow: "auto",
              paddingLeft: "24px",
              "&::-webkit-scrollbar": {
                width: "12px"
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.05)"
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(255, 255, 255, 0.3)",
                borderRadius: "6px",
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.4)"
                }
              },
              scrollbarWidth: "thin",
              scrollbarColor:
                "rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.05)"
            }}
          >
            <SettingsMenu />
          </Box>
          <Box
            sx={{
              marginLeft: "280px", // 240px sidebar + 24px gap
              flex: 1,
              overflow: "auto",
              height: "100%",
              padding: "0px 24px",
              "&::-webkit-scrollbar": {
                width: "12px"
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.05)"
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(255, 255, 255, 0.3)",
                borderRadius: "6px",
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.4)"
                }
              },
              scrollbarWidth: "thin",
              scrollbarColor:
                "rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.05)"
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

const menuItems = [
  // {
  //     label: "Patient Enrollment",
  //     icon: <PersonAddAlt1Outlined />,
  //     path: "/dashboard/settings/patient-enrollment"
  // },
  // {
  //     label: "Providers",
  //     icon: <PersonOutlineOutlined />,
  //     path: "/dashboard/settings/providers"
  // },
  {
    label: "Agent",
    icon: <Person />,
    path: "/dashboard/settings/agent"
  },
  {
    label: "Functional Modules",
    icon: <DashboardCustomizeOutlined />,
    path: "/dashboard/settings/functional-modules"
  },
  {
    label: "Appointment Types",
    icon: <EventNoteOutlined />,
    path: "/dashboard/settings/appointment-types"
  },
  {
    label: "Insurances",
    icon: <HealthAndSafetyOutlined />,
    path: "/dashboard/settings/insurances"
  },
  {
    label: "Payment Processing",
    icon: <PaymentOutlined />,
    path: "/dashboard/settings/payment-processing"
  },
  {
    label: "Miscellaneous",
    icon: <PendingActionsOutlined />,
    path: "/dashboard/settings/miscellaneous"
  },
  {
    label: "Automation Settings",
    icon: <PersonAddAlt1Outlined />,
    path: "/dashboard/settings/auto-add"
  }
  // {
  //     label: "Payers",
  //     icon: <LocalAtmOutlined />,
  //     path: "/dashboard/settings/payers"
  // },
  // {
  //     label: "Care Plans",
  //     icon: <AssignmentOutlined />,
  //     path: "/dashboard/settings/care-plans"
  // },
  // {
  //     label: "Waitlist",
  //     icon: <PendingActionsOutlined />,
  //     path: "/dashboard/settings/waitlist"
  // }
];

function SettingsMenu() {
  const location = useLocation();

  return (
    <List
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "240px"
      }}
    >
      {menuItems.map((item) => (
        <ListItem key={item.path} disablePadding>
          <ListItemButton
            selected={location.pathname.startsWith(item.path)}
            component={Link}
            to={item.path}
            sx={{ borderRadius: "8px" }}
          >
            <ListItemIcon
              sx={{ fontSize: "20px", minWidth: "40px !important" }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );
}
